import { useQuery } from '@tanstack/react-query';
import { createActivitiesApi, ActivityFilters, DayActivityFilters } from '../api/activities.api';
import { auth } from '@core/utils/firebase/firebase';
import { Auth } from '@angular/fire/auth';
import { canBuildDayBounds } from '../api/activity-date-bounds';

const activitiesApi = createActivitiesApi(auth as Auth);

export function useActivityByIdQuery(id: number) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['activity', id],
    queryFn: () => activitiesApi.getActivityById(id),
    enabled: !!id,
  });

  return {
    activity: data,
    isLoading,
    error,
  };
}

export function useActivitiesForMonth(filters: ActivityFilters) {
  const { siteId, selectedMonth, timezone } = filters;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'activities',
      siteId,
      selectedMonth.getFullYear(),
      selectedMonth.getMonth(),
      timezone,
    ],
    queryFn: async () => {
      const result = await activitiesApi.getActivitiesForMonth(filters);
      return result;
    },
    enabled: !!siteId && !!selectedMonth && !!timezone,
    staleTime: 30 * 1000, // 30 seconds - calendar data can be slightly stale
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for quick navigation
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Enable background refetch for data freshness
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  return {
    data: data || [],
    isLoading,
    error,
    refetch,
  };
}

export function useActivitiesForDay(filters: DayActivityFilters) {
  const { siteId, selectedDate, timezone } = filters;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'activities-day',
      siteId,
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      timezone,
    ],
    queryFn: () => activitiesApi.getActivitiesForDay(filters),
    // `!!selectedDate` is not enough: an Invalid Date is truthy, and it used to
    // reach the API as `eventStart=NaN`, which triggered a full-table scan.
    enabled: !!siteId && canBuildDayBounds(selectedDate, timezone),
    staleTime: 30 * 1000, // 30 seconds - calendar data can be slightly stale
    gcTime: 5 * 60 * 1000, // 5 minutes - keep in cache for quick navigation
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Enable background refetch for data freshness
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  return {
    data: data || [],
    isLoading,
    error,
    refetch,
  };
}
